import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ngfModule } from 'angular-file';
import { NgSelectModule } from '@ng-select/ng-select';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { QuillModule } from 'ngx-quill';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import {
  PdfViewerModule, LinkAnnotationService, BookmarkViewService, MagnificationService, ThumbnailViewService,
  ToolbarService, NavigationService, TextSearchService, TextSelectionService, PrintService
} from '@syncfusion/ej2-angular-pdfviewer';
import { SignaturePadModule } from '@viablelogic/ngx-signature-pad';

//Directive imports
import { PhoneFormatDirective } from './directive/phone-format.directive';
import { USZipFormatDirective } from './directive/uszip-format.directive';
import { TimeValidateDirective } from './directive/time-validate.directive';
import { PercentNumberDirective } from './directive/percent-number.directive';
import { CheckBoxYNDirective } from './directive/check-box-yn.directive';
import { ElasticDirective } from './directive/elastic.directive';
import { NavigableDirective } from './directive/navigable.directive';
import { SelectAllOnClickDirective } from './directive/select-all-on-click.directive';
import { NumericValidateDirective } from './directive/numeric-validate.directive';
import { UintValidateDirective } from './directive/uint-validate.directive';
import { DateValidateDirective } from './directive/date-validate.directive';
import { UrlValidateDirective } from './directive/url-validate.directive';
import { WholeNumberValidateDirective } from './directive/wholenumber-validate.directive';
import { NumericRangeByTypeValidateDirective } from './directive/numericrangebytype-validate.directive';
import { LostFocusDirective } from './directive/lost-focus.directive';

//Pipe imports
import { ExcludeFromPipe } from './pipe/exclude-from.pipe';
import { OrderByPipe } from './pipe/order-by.pipe';
import { DOMSafePipe } from './pipe/domsafe.pipe';
import { DaysAndHoursPipe } from './pipe/days-and-hours.pipe';

//Component imports
import { SearchDropDownComponent } from './component/search-drop-down/search-drop-down.component';
import { FileUploadComponent } from './component/file-upload/file-upload.component';
import { MultipleDateSelectorComponent } from './component/multiple-date-selector/multiple-date-selector.component';
import { SsnFormatDirective } from './directive/ssn-format.directive';
import { FilereadDirective } from './directive/fileread.directive';
import { RichTextEditorComponent } from './component/rich-text-editor/rich-text-editor.component';
import { AutoCompleteEmailComponent } from './component/auto-complete-email/auto-complete-email.component';
import { CrewTimecardGridComponent } from './component/crew-timecard-grid/crew-timecard-grid.component';
import { TimeWorkedComponent } from './component/time-worked/time-worked.component';
import { AutoCompleteEmailTCBComponent } from './component/auto-complete-email-t-c-b/auto-complete-email-t-c-b.component';
import { CustomAutofocusDirective } from './directive/custom-autofocus.directive';
import { WebixDatatablecomponent } from './component/time-off-grid/webix-datatable.component';
import { HRIMPdfViewerComponent } from './component/pdf-viewer/pdf-viewer.component';
import { OrderByWithParsingPipe } from './pipe/order-by-with-parsing.pipe';
import { DateTwentyFourHoursDirective } from './directive/date-twenty-four-hours.directive';
import { ServiceManagerFilterComponent } from './component/service-manager-filter/service-manager-filter.component';
import { LoginFooterComponent } from './component/login-footer/login-footer.component';
import { ReadOnlyPdfViewerComponent } from './component/read-only-pdf-viewer/read-only-pdf-viewer.component';
import { OrderByAlphanumPipe } from './pipe/order-by-alphanum.pipe';
import { KeypressEventsComponent } from './component/keypress-events/keypress-events.component';
import { TimecardReviewGridComponent } from './component/timecard-review-grid/timecard-review-grid.component';
import { FilterPipe } from './pipe/filter.pipe';
import { UdFieldComponent } from './component/udField/ud-field/ud-field.component';
import { DecimalLengthValidateDirective } from './directive/decimallength-validate.directive';

//Sort order for declarations/exports - Directives,Pipes,Components
@NgModule({
  declarations: [
    PhoneFormatDirective,
    USZipFormatDirective,
    TimeValidateDirective,
    PercentNumberDirective,
    CheckBoxYNDirective,
    ExcludeFromPipe,
    OrderByPipe,
    DOMSafePipe,
    DaysAndHoursPipe,
    SearchDropDownComponent,
    PercentNumberDirective,
    FileUploadComponent,
    SsnFormatDirective,
    MultipleDateSelectorComponent,
    FilereadDirective,
    RichTextEditorComponent,
    AutoCompleteEmailComponent,
    CrewTimecardGridComponent,
    TimeWorkedComponent,
    ElasticDirective,
    AutoCompleteEmailTCBComponent,
    CustomAutofocusDirective,
    WebixDatatablecomponent,
    HRIMPdfViewerComponent,
    NavigableDirective,
    SelectAllOnClickDirective,
    OrderByWithParsingPipe,
    ServiceManagerFilterComponent,
    DateTwentyFourHoursDirective,
    LoginFooterComponent,
    ReadOnlyPdfViewerComponent,
    OrderByAlphanumPipe,
    KeypressEventsComponent,
    NumericValidateDirective,
    UintValidateDirective,
    DateValidateDirective,
    LostFocusDirective,
    TimecardReviewGridComponent,
    FilterPipe,
    UdFieldComponent,
    UrlValidateDirective,
    WholeNumberValidateDirective,
    NumericRangeByTypeValidateDirective,
    DecimalLengthValidateDirective
  ],
  imports: [
    CommonModule,
    FormsModule,
    ngfModule,
    NgSelectModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    QuillModule,
    CollapseModule,
    NgbModule,
    PdfViewerModule,
    SignaturePadModule,
    BsDatepickerModule.forRoot()
  ],
  exports: [
    PhoneFormatDirective,
    USZipFormatDirective,
    TimeValidateDirective,
    PercentNumberDirective,
    CheckBoxYNDirective,
    ElasticDirective,
    ExcludeFromPipe,
    OrderByPipe,
    DOMSafePipe,
    DaysAndHoursPipe,
    SearchDropDownComponent,
    PercentNumberDirective,
    FileUploadComponent,
    SsnFormatDirective,
    MultipleDateSelectorComponent,
    FilereadDirective,
    RichTextEditorComponent,
    AutoCompleteEmailComponent,
    CrewTimecardGridComponent,
    TimeWorkedComponent,
    AutoCompleteEmailTCBComponent,
    CustomAutofocusDirective,
    WebixDatatablecomponent,
    HRIMPdfViewerComponent,
    NavigableDirective,
    SelectAllOnClickDirective,
    OrderByWithParsingPipe,
    DateTwentyFourHoursDirective,
    ServiceManagerFilterComponent,
    LoginFooterComponent,
    ReadOnlyPdfViewerComponent,
    KeypressEventsComponent,
    NumericValidateDirective,
    UintValidateDirective,
    DateValidateDirective,
    LostFocusDirective,
    TimecardReviewGridComponent,
    FilterPipe,
    UdFieldComponent,
    UrlValidateDirective,
    WholeNumberValidateDirective,
    NumericRangeByTypeValidateDirective,
    DecimalLengthValidateDirective
  ],
  providers: [LinkAnnotationService, BookmarkViewService, MagnificationService,
    ThumbnailViewService, ToolbarService, NavigationService, TextSearchService, TextSelectionService, PrintService]    
})
export class SharedModule { }
